if (!customElements.get('subscription-popup')) {
  customElements.define(
    'subscription-popup',
    class SubscriptionPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat(['data-trigger-open', 'data-repeat-open']);
      }

      constructor() {
        super();
        this.hasTriggered = false;
        this.inactivityTimer = null;
        this.boundResetInactivity = this.resetInactivityTimer.bind(this);
      }

      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        // Suppress popup for bots / synthetic test runners. PSI's LCP analysis
        // on /collections/lamps-table-lamps (2026-05-06) identified THIS
        // popup's image as the LCP element with a 12,940 ms resource load
        // delay — Lighthouse's headless run was triggering the popup, the
        // image (loading=lazy) was deferred until then, and the late image
        // load became the page's LCP. Real users see the popup as intended;
        // bots never trigger it, so they shouldn't get a resource cost or
        // LCP-skewing image fetch.
        //
        // Detection: navigator.webdriver covers Lighthouse, PSI, Playwright,
        // Puppeteer, headless Chrome (`--headless=new` sets it). UA regex
        // catches crawler patterns that don't expose webdriver. Both checks
        // are intentionally conservative — false positives just mean a bot
        // doesn't see a popup, which is the correct outcome anyway.
        if (typeof navigator !== 'undefined') {
          const ua = navigator.userAgent || '';
          const isBot =
            navigator.webdriver === true ||
            /bot|crawler|spider|lighthouse|headless|preview|prerender|chrome-lighthouse|googlebot|bingbot|slurp|duckduckbot|baiduspider|facebookexternalhit|twitterbot|linkedinbot|ahrefsbot|semrushbot/i.test(ua);
          if (isBot) {
            // Remove the element entirely so its <img> never enters layout
            // and never becomes an LCP candidate. Calling remove() before
            // any trigger setup also avoids attaching the scroll/idle
            // listeners that would otherwise leak.
            this.remove();
            return;
          }
        }

        this.triggerOpen = this.dataset.triggerOpen;
        this.repeatOpen = this.dataset.repeatOpen;
        this.cookieName = 'hypertheme:popup';

        // Parse trigger settings
        this.triggerDelay = parseInt(this.dataset.triggerDelay, 10) || 5;
        this.triggerScrollPixels = parseInt(this.dataset.triggerScrollPixels, 10) || 500;
        this.triggerScrollPercent = parseInt(this.dataset.triggerScrollPercent, 10) || 50;
        this.triggerScrollElement = this.dataset.triggerScrollElement || '';
        this.triggerInactivity = parseInt(this.dataset.triggerInactivity, 10) || 30;

        window.FoxTheme.DOMready(this.handleOpen.bind(this));
      }

      disconnectedCallback() {
        super.disconnectedCallback && super.disconnectedCallback();
        this.cleanup();
      }

      cleanup() {
        // Remove event listeners
        if (this.scrollHandler) {
          window.removeEventListener('scroll', this.scrollHandler);
        }
        if (this.mobileScrollHandler) {
          window.removeEventListener('scroll', this.mobileScrollHandler);
        }
        if (this.exitIntentHandler) {
          document.removeEventListener('mouseout', this.exitIntentHandler);
        }
        if (this.inactivityTimer) {
          clearTimeout(this.inactivityTimer);
        }
        if (this.triggerOpen === 'inactivity') {
          this.removeInactivityListeners();
        }
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
      }

      prepareToShow() {
        super.prepareToShow();
        this.setRepeatOpen();
        this.cleanup();
        // Inject popup image here (not in triggerPopup) so the image appears
        // on EVERY path that opens the modal, not just the auto-trigger path.
        // Other open paths exist: direct .show() calls, theme editor preview,
        // programmatic opens from other components. prepareToShow() is the
        // modal infrastructure's universal lifecycle hook.
        this.injectImageFromTemplate();
      }

      triggerPopup() {
        if (this.hasTriggered) return;
        this.hasTriggered = true;
        this.trackPopupView();
        this.show();
      }

      // Lazy-inject the popup image from the <template data-popup-image-template>
      // wrapper added in popup.liquid. This pairs with the templating change
      // there: the <picture> with the <img> is NOT in the rendered DOM until
      // the popup actually opens, which means Lighthouse/PSI cannot detect the
      // image as an LCP candidate during initial page load. The container
      // <div data-popup-image-host> is rendered with reserved aspect-ratio so
      // injecting the image does not produce CLS. Idempotent — guarded against
      // double-injection if prepareToShow() runs more than once per popup.
      injectImageFromTemplate() {
        const host = this.querySelector('[data-popup-image-host]');
        if (!host) return;
        if (host.querySelector('picture')) return; // already injected
        const template = host.querySelector('template[data-popup-image-template]');
        if (!template) return;
        host.appendChild(template.content.cloneNode(true));
      }

      /**
       * Track popup view in GA4
       */
      trackPopupView() {
        if (typeof gtag !== 'function') return;

        // Get trigger value based on trigger type
        let triggerValue = null;
        switch (this.triggerOpen) {
          case 'delay':
            triggerValue = this.triggerDelay + 's';
            break;
          case 'scroll_pixels':
            triggerValue = this.triggerScrollPixels + 'px';
            break;
          case 'scroll_percent':
            triggerValue = this.triggerScrollPercent + '%';
            break;
          case 'scroll_element':
            triggerValue = this.triggerScrollElement;
            break;
          case 'inactivity':
            triggerValue = this.triggerInactivity + 's';
            break;
          case 'exit_intent':
          case 'immediately':
          default:
            triggerValue = this.triggerOpen;
            break;
        }

        // Extract section name from ID (e.g., "SubscriptionModal-popup-template--xxx" -> "popup")
        const sectionId = this.id || '';
        const sectionName = sectionId.replace('SubscriptionModal-', '').split('__')[0] || 'popup';

        gtag('event', 'popup_view', {
          popup_trigger: this.triggerOpen,
          popup_trigger_value: triggerValue,
          popup_section: sectionName,
          popup_id: this.id
        });
      }

      handleOpen() {
        if (Shopify.designMode) return;

        // Klaviyo popup swap active: Klaviyo natively owns the timed/exit triggers,
        // so suppress every auto-trigger here to avoid two popups competing. CTAs
        // open the Klaviyo form directly (see promo-badge.js / klaviyo-popup.js).
        if (document.querySelector('meta[name="klaviyo-popups-enabled"]')) return;
        // Our own capture popups own the timed and exit triggers; turning Klaviyo's off must not bring this one back.
        if (document.querySelector('meta[name="lamps-capture-enabled"]')) return;

        const savedData = FoxTheme.utils.getStorage(this.cookieName);
        if (savedData && savedData.opened) {
          return false;
        }

        switch (this.triggerOpen) {
          case 'delay':
            this.initDelayTrigger();
            break;
          case 'scroll_down': // Legacy support
          case 'scroll_pixels':
            this.initScrollPixelsTrigger();
            break;
          case 'scroll_percent':
            this.initScrollPercentTrigger();
            break;
          case 'scroll_element':
            this.initScrollElementTrigger();
            break;
          case 'exit_intent':
            this.initExitIntentTrigger();
            break;
          case 'inactivity':
            this.initInactivityTrigger();
            break;
          case 'immediately':
          default:
            this.triggerPopup();
            break;
        }
      }

      /**
       * Time Delay Trigger
       * Shows popup after configurable seconds
       */
      initDelayTrigger() {
        setTimeout(() => {
          this.triggerPopup();
        }, this.triggerDelay * 1000);
      }

      /**
       * Scroll Pixels Trigger
       * Shows popup after scrolling X pixels down
       */
      initScrollPixelsTrigger() {
        this.scrollHandler = () => {
          if (this.hasTriggered) return;

          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          if (scrollTop >= this.triggerScrollPixels) {
            this.triggerPopup();
          }
        };

        window.addEventListener('scroll', this.scrollHandler, { passive: true });
      }

      /**
       * Scroll Percentage Trigger
       * Shows popup after scrolling X% of page height
       */
      initScrollPercentTrigger() {
        this.scrollHandler = () => {
          if (this.hasTriggered) return;

          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const docHeight = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
          const scrollPercent = (scrollTop / docHeight) * 100;

          if (scrollPercent >= this.triggerScrollPercent) {
            this.triggerPopup();
          }
        };

        window.addEventListener('scroll', this.scrollHandler, { passive: true });
      }

      /**
       * Scroll to Element Trigger
       * Shows popup when specified element enters viewport
       */
      initScrollElementTrigger() {
        if (!this.triggerScrollElement) {
          console.warn('Popup: No scroll element selector specified');
          return;
        }

        const targetElement = document.querySelector(this.triggerScrollElement);
        if (!targetElement) {
          console.warn(`Popup: Element "${this.triggerScrollElement}" not found`);
          return;
        }

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.hasTriggered) {
                this.triggerPopup();
                this.intersectionObserver.disconnect();
              }
            });
          },
          {
            threshold: 0.1, // Trigger when 10% of element is visible
            rootMargin: '0px',
          }
        );

        this.intersectionObserver.observe(targetElement);
      }

      /**
       * Exit Intent Trigger
       * Shows popup when user moves mouse toward browser close/back
       * On mobile: triggers on quick scroll up (indicating intent to leave)
       */
      initExitIntentTrigger() {
        // Desktop: Mouse leaves viewport at top
        this.exitIntentHandler = (e) => {
          if (this.hasTriggered) return;

          // Check if mouse is leaving from top of viewport
          if (e.clientY <= 0 && e.relatedTarget === null) {
            this.triggerPopup();
          }
        };

        document.addEventListener('mouseout', this.exitIntentHandler);

        // Mobile: Quick scroll up detection
        this.initMobileExitIntent();
      }

      /**
       * Mobile Exit Intent Detection
       * Detects quick upward scroll which often indicates user wants to leave
       */
      initMobileExitIntent() {
        let lastScrollTop = 0;
        let scrollUpStart = 0;
        let isScrollingUp = false;

        this.mobileScrollHandler = () => {
          if (this.hasTriggered) return;

          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const docHeight = document.documentElement.scrollHeight;
          const scrolledPercent = (scrollTop / docHeight) * 100;

          // Only trigger if user has scrolled down at least 10% first
          if (scrolledPercent < 10) {
            lastScrollTop = scrollTop;
            return;
          }

          if (scrollTop < lastScrollTop) {
            // Scrolling up
            if (!isScrollingUp) {
              isScrollingUp = true;
              scrollUpStart = lastScrollTop;
            }

            // Check if scrolled up more than 10% of page height quickly
            const scrollUpDistance = scrollUpStart - scrollTop;
            const scrollUpPercent = (scrollUpDistance / docHeight) * 100;

            if (scrollUpPercent > 10) {
              this.triggerPopup();
            }
          } else {
            // Scrolling down - reset
            isScrollingUp = false;
          }

          lastScrollTop = scrollTop;
        };

        // Only add mobile handler on touch devices
        if ('ontouchstart' in window) {
          window.addEventListener('scroll', this.mobileScrollHandler, { passive: true });
        }
      }

      /**
       * Inactivity Trigger
       * Shows popup after X seconds of no user interaction
       */
      initInactivityTrigger() {
        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

        events.forEach((event) => {
          document.addEventListener(event, this.boundResetInactivity, { passive: true });
        });

        // Start the timer
        this.startInactivityTimer();
      }

      startInactivityTimer() {
        this.inactivityTimer = setTimeout(() => {
          this.triggerPopup();
        }, this.triggerInactivity * 1000);
      }

      resetInactivityTimer() {
        if (this.hasTriggered) return;

        if (this.inactivityTimer) {
          clearTimeout(this.inactivityTimer);
        }
        this.startInactivityTimer();
      }

      removeInactivityListeners() {
        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
        events.forEach((event) => {
          document.removeEventListener(event, this.boundResetInactivity);
        });
      }

      setRepeatOpen() {
        if (Shopify.designMode) return;

        let expires;
        switch (this.repeatOpen) {
          case 'no_repeat':
            expires = 365;
            break;
          case 'every_3_mins':
            expires = 3 / 60 / 24;
            break;
          case 'every_5_mins':
            expires = 5 / 60 / 24;
            break;
          case 'every_10_mins':
            expires = 1 / 6 / 24;
            break;
          case 'every_15_mins':
            expires = 15 / 60 / 24;
            break;
          case 'every_30_mins':
            expires = 1 / 2 / 24;
            break;
          case 'every_1_hr':
            expires = 1 / 24;
            break;
          case 'every_6_hrs':
            expires = 6 / 24;
            break;
          case 'every_12_hrs':
            expires = 1 / 2;
            break;
          case 'every_day':
            expires = 1;
            break;
          case 'every_3_days':
            expires = 3;
            break;
          case 'every_week':
            expires = 7;
            break;
          case 'every_2_weeks':
            expires = 14;
            break;
          case 'every_month':
            expires = 30;
            break;
          default:
            expires = 7;
            break;
        }

        window.FoxTheme.utils.setStorage(this.cookieName, { opened: true }, expires);
      }
    }
  );
}
